import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'expo-router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMMKVString } from 'react-native-mmkv';

import { customerStateQueryOptions } from '@/api/customer/customerStateQuery';
import { type BottomSheetRef } from '@/components/_shared/BottomSheet';
import { AppointmentRatingPromptModal } from '@/components/appointment/AppointmentRatingPromptModal';
import { storage, storageKeys } from '@/constants/storage';
import { useAppointmentRatingPromptEligibility } from '@/hooks/customer/useAppointmentRatingPromptEligibility';
import { useDismissNotificationMutation } from '@/hooks/mutations/customer/useDismissNotificationMutation';
import { Logger } from '@/utils/logging';

interface AppointmentRatingPromptContextType {
  triggerRatingCheck: (customerId: string) => void;
  /** Dev-only: force-open the modal against the customer's latest past appointment, bypassing eligibility. */
  triggerRatingPromptPreview: () => Promise<void>;
}

const AppointmentRatingPromptContext = createContext<
  AppointmentRatingPromptContextType | undefined
>(undefined);

export interface AppointmentRatingPromptProviderProps {
  children: React.ReactNode;
}

// Legacy parity: only surface the prompt on these browsing/landing routes.
const WHITELISTED_ROUTE_PREFIXES = [
  '/booking/brands',
  '/booking/book',
  '/booking/gift-card',
];

const isWhitelistedRoute = (pathname: string): boolean =>
  pathname === '/' ||
  WHITELISTED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export function AppointmentRatingPromptProvider({
  children,
}: AppointmentRatingPromptProviderProps) {
  // Seed from persisted storage so already-logged-in users are checked on app start.
  const [persistedCustomerId = ''] = useMMKVString(
    storageKeys.customerId,
    storage,
  );
  const [customerId, setCustomerId] = useState<string | null>(null);
  const effectiveCustomerId = customerId ?? persistedCustomerId;

  const pathname = usePathname();
  // Once-per-session guard (legacy parity: no cross-session cooldown).
  const promptShownThisSession = useRef(false);
  const queryClient = useQueryClient();

  const [activeAppointmentId, setActiveAppointmentId] = useState<string | null>(
    null,
  );
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  const { pendingNotification } =
    useAppointmentRatingPromptEligibility(effectiveCustomerId);

  const dismissMutation = useDismissNotificationMutation({
    customerId: effectiveCustomerId,
  });

  useEffect(() => {
    if (
      pendingNotification &&
      !activeAppointmentId &&
      !promptShownThisSession.current &&
      isWhitelistedRoute(pathname)
    ) {
      Logger.console(
        '[AppointmentRating] pending rating resolved, opening modal',
        {
          appointmentId: pendingNotification.resourceId,
        },
      );
      promptShownThisSession.current = true;
      setActiveAppointmentId(pendingNotification.resourceId);
    }
  }, [pendingNotification, activeAppointmentId, pathname]);

  const triggerRatingCheck = useCallback((id: string) => {
    Logger.console('[AppointmentRating] triggerRatingCheck called', {
      customerId: id,
    });
    setCustomerId(id);
  }, []);

  const triggerRatingPromptPreview = useCallback(async (): Promise<void> => {
    const previewCustomerId = storage.getString(storageKeys.customerId);
    if (!previewCustomerId) {
      Logger.warn(
        '[AppointmentRating] preview triggered with no signed-in customer',
      );
      return;
    }

    let state;
    try {
      state = await queryClient.fetchQuery(
        customerStateQueryOptions(previewCustomerId),
      );
    } catch (error) {
      Logger.error(
        '[AppointmentRating] preview failed to load customer state',
        {
          error,
        },
      );
      return;
    }

    const latestPastAppointment = [...(state.pastAppointments ?? [])].sort(
      (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
    )[0];

    if (!latestPastAppointment) {
      Logger.warn(
        '[AppointmentRating] preview triggered with no past appointments to rate',
      );
      return;
    }

    Logger.console(
      '[AppointmentRating] preview triggered — bypassing eligibility',
      { appointmentId: latestPastAppointment.id },
    );
    promptShownThisSession.current = true;
    setActiveAppointmentId(latestPastAppointment.id);
  }, [queryClient]);

  // Closed without rating (close button / swipe / backdrop): only hide for this
  // session. The once-per-session guard prevents re-showing until the next app
  // launch, and nothing is dismissed server-side so the reviews remain pending.
  const handleClose = useCallback(() => {
    setActiveAppointmentId(null);
  }, []);

  // Rated: dismiss ONLY the rated appointment's notification, leaving the other
  // pending reviews so the user can rate them in future sessions. Guarded by
  // resourceId match because the preview trigger can open an appointment that
  // differs from pendingNotification, and that one has no notification to
  // dismiss.
  const handleSubmitted = useCallback(() => {
    if (pendingNotification?.resourceId === activeAppointmentId) {
      dismissMutation.mutate({ ids: [pendingNotification.id] });
    }
    setActiveAppointmentId(null);
  }, [pendingNotification, activeAppointmentId, dismissMutation]);

  const value: AppointmentRatingPromptContextType = {
    triggerRatingCheck,
    triggerRatingPromptPreview,
  };

  return (
    <AppointmentRatingPromptContext.Provider value={value}>
      {children}
      {activeAppointmentId && (
        <AppointmentRatingPromptModal
          ref={bottomSheetRef}
          appointmentId={activeAppointmentId}
          onClose={handleClose}
          onSubmitted={handleSubmitted}
        />
      )}
    </AppointmentRatingPromptContext.Provider>
  );
}

export function useAppointmentRatingPromptContext(): AppointmentRatingPromptContextType {
  const context = useContext(AppointmentRatingPromptContext);
  if (!context) {
    throw new Error(
      'useAppointmentRatingPromptContext must be used within AppointmentRatingPromptProvider',
    );
  }
  return context;
}
